import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { Field } from 'formik';
import { CashieringRoute, BalanceTotals, Transfer, TransferIRAContributionType } from '@tradingblock/types';
import {
  FormGroup,
  CurrencyField,
  SelectDropdownInput,
  formatNumber,
  CheckboxInput,
  ContactEmail,
  ContactPhone,
  GetTradingBlockSiteName,
} from '@tradingblock/components';
import { CashieringViewFormValues, CashieringViewProps, ViewOnValidateType } from './data/useCashieringView';
import { validateIraContribution, validateTransferAmount } from './data/cashieringValidation';
import { cashieringSelectors, useStateSelector, subAccountIdSelector } from '../../../data/global/dataSelectors';
import { account } from '../../../data/global/selectors/accountSelectors';
import { Actions } from '../../../data/global/actions';
import { useDispatcher } from '../../../data/global/hooks';
import { getContributionDescription, getFormattedTransferIRAContributionType } from '../../../utilities/cashiering';
import { ScheduledTransferFields } from './ScheduledTransferFields';

interface DepositAmountViewContentProps extends CashieringViewProps<CashieringViewFormValues> {
  getAccountBalances: (subaccountId?: number) => void;
  totals: BalanceTotals;
  nickname: string;
  isIRA?: boolean;
  scheduledTransferMode?: boolean;
}

const DepositAmountViewContent: React.FC<DepositAmountViewContentProps> = ({
  values,
  setValues,
  getAccountBalances,
  totals,
  nickname,
  children,
  scheduledTransferMode,
}) => {
  const globalSubAccountId = useStateSelector(subAccountIdSelector);
  const [transferSubAccountId, setTransferSubAccountId] = useState<number>();

  const subAccounts = useStateSelector(account.subAccounts);
  const masterAccount = useStateSelector(account.masterAccount);
  const currentSelectedSubAccountId = useStateSelector(account.subAccountId);
  const subAccountOptions = useMemo(() => {
    return _.map(subAccounts, sa => ({
      value: sa.Id,
      label: `${sa.Nickname} (${sa.AcctSuffix})`,
    }));
  }, [subAccounts]);
  const accountId = useStateSelector(account.accountId);
  const subAccountBalances = useStateSelector(account.subAccountsBalances);
  const dispatch = useDispatch();
  const isIraAccount = useStateSelector(account.accountIsIRA);
  const cashieringProfile = useStateSelector(s => s.cashiering.cashieringProfile);
  const { iraConstraints } = cashieringProfile;
  const { contribution } = iraConstraints ? iraConstraints : { contribution: undefined };
  const isNotEligible = contribution && (!contribution.ach.allowedTypes || contribution.ach.allowedTypes.length === 0);
  const siteName = GetTradingBlockSiteName();

  useEffect(() => {
    if (subAccounts && subAccounts.length > 0 && accountId) {
      dispatch(Actions.requestSubAccountBalances({ accountId, subAccounts: subAccounts }));
    }
  }, [subAccounts, dispatch, accountId]);

  useEffect(() => {
    if (values.transfer && values.transfer.subAccountId) {
      setTransferSubAccountId(values.transfer.subAccountId);
    }
    if (globalSubAccountId) {
      // If globally selected sub account, force select the dropdown
      setTransferSubAccountId(globalSubAccountId);
      setValues({ ...values, transfer: { ...values.transfer, subAccountId: globalSubAccountId } });
    }
  }, []);

  useEffect(() => {
    if (
      subAccounts &&
      !_.isEmpty(subAccounts) &&
      (globalSubAccountId === undefined || globalSubAccountId === undefined)
    ) {
      setValues({
        ...values,
        transfer: {
          ...values.transfer,
          amount: values.transfer
            ? Object.values(values.transfer.subAccountsValues ? values.transfer.subAccountsValues : {}).reduce(
                (acc: number, v) => {
                  if (typeof v === 'string') {
                    //Since the currency component will convert it to a string, need to convert it back to a number
                    let value = Number(v.replace(/[^0-9.-]+/g, ''));
                    return acc + value;
                  } else if (typeof v === 'number') {
                    //But if the user has already entered a number, manually, just use it
                    return acc + v;
                  }
                  return acc;
                },
                0 as number
              )
            : 0,
        },
      });
    }
  }, [subAccounts, values.transfer ? values.transfer.subAccountsValues : false]);

  useEffect(() => {
    if (globalSubAccountId === undefined) {
      if (masterAccount !== undefined) {
        setTransferSubAccountId(masterAccount.Id);
        setValues({ ...values, transfer: { ...values.transfer, subAccountId: masterAccount.Id } });
      }
    }
  }, [globalSubAccountId]);

  const onSelectSubAccount = useCallback(
    (id: number) => {
      setTransferSubAccountId(id);
      setValues({ ...values, transfer: { ...values.transfer, amount: 0, subAccountId: id } });
      getAccountBalances(id);
    },
    [setTransferSubAccountId, values]
  );

  const onSelectContributionType = useCallback(
    (value: { type: string; previousYearContributionAllowed: boolean }) => {
      setValues({
        ...values,
        transfer: {
          ...values.transfer,
          iraContribution: {
            ...(values.transfer && values.transfer.iraContribution),
            type: value.type as TransferIRAContributionType,
            applyToPriorYear: false,
            previousYearContributionAllowed: value.previousYearContributionAllowed,
          },
        },
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );

  const shouldDisplayCheckbox = (
    contribution: { canMakeContributionsForPriorYear: boolean },
    values: Partial<CashieringViewFormValues>
  ) => {
    return (
      contribution &&
      contribution.canMakeContributionsForPriorYear &&
      values &&
      values.transfer &&
      values.transfer.iraContribution &&
      values.transfer.iraContribution.type &&
      values.transfer.iraContribution.previousYearContributionAllowed
    );
  };

  const handleCheckboxChange = (
    values: Partial<CashieringViewFormValues>,
    setValues: (arg0: any) => void,
    value: boolean
  ) => {
    if (values && values.transfer && values.transfer.iraContribution && values.transfer.iraContribution.type) {
      setValues({
        ...values,
        transfer: {
          ...values.transfer,
          iraContribution: {
            ...values.transfer.iraContribution,
            applyToPriorYear: value,
            type: values.transfer.iraContribution.type,
          },
        },
      });
    }
  };

  return (
    <>
      <div className="screen-body-section">
        <p>
          <span className="mute">Deposit to:</span> {nickname}.
        </p>
        <FormGroup title={'How much do you wish to deposit?'}>
          <Field
            component={CurrencyField}
            disabled={isIraAccount && isNotEligible}
            id="transfer.amount"
            label="Amount"
          />
        </FormGroup>
        {scheduledTransferMode && <ScheduledTransferFields values={values} />}
        {isIraAccount && (
          <>
            {contribution && contribution.ach.allowedTypes && (
              <FormGroup title="Type of contribution">
                <SelectDropdownInput
                  name="transfer.iraContribution.type"
                  label="Contribution type"
                  options={contribution.ach.allowedTypes.map(type => ({
                    value: { type: type.type, previousYearContributionAllowed: type.previousYearContributionAllowed },
                    label: getFormattedTransferIRAContributionType(type.type),
                    data: type.previousYearContributionAllowed,
                  }))}
                  onchange={(e, value) => onSelectContributionType(value)}
                  value={
                    values.transfer && values.transfer.iraContribution
                      ? getFormattedTransferIRAContributionType(values.transfer.iraContribution.type)
                      : undefined
                  }
                  menuPlacement="auto"
                />
                {values.transfer && values.transfer.iraContribution && values.transfer.iraContribution.type && (
                  <div className="mute txt-sm">
                    {getContributionDescription(
                      values.transfer && values.transfer.iraContribution
                        ? values.transfer.iraContribution.type
                        : undefined
                    )}
                  </div>
                )}
              </FormGroup>
            )}
            {contribution && shouldDisplayCheckbox(contribution, values) && (
              <FormGroup title={`Is this contribution for the prior year? (${new Date().getFullYear() - 1})`}>
                <Field
                  component={CheckboxInput}
                  type="checkbox"
                  name="transfer.iraContribution.applyToPriorYear"
                  label="Yes"
                  checked={
                    values &&
                    values.transfer &&
                    values.transfer.iraContribution &&
                    values.transfer.iraContribution.applyToPriorYear
                  }
                  onchange={(e: any, value: any) => handleCheckboxChange(values, setValues, value)}
                />
              </FormGroup>
            )}
            {isNotEligible && (
              <div className="alert alert-warning">
                <p>
                  You are not eligible to make an IRA contribution at this time. Please contact {siteName} for more
                  information.
                </p>
                <p>
                  Email: <ContactEmail />
                </p>
                <p>
                  Phone: <ContactPhone />
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {children}
    </>
  );
};

const DepositAmountView: React.FC<
  CashieringViewProps<CashieringViewFormValues> & { scheduledTransferMode?: boolean }
> = props => {
  const { values, scheduledTransferMode } = props;
  const totals = useSelector(cashieringSelectors.balanceTotals);
  const subAccounts = useStateSelector(account.subAccounts);
  const accountInfo = useStateSelector(s =>
    s.account
      ? {
          nickname: s.account.nickname,
          accountNumber: s.account.accountNumber,
          isIRA: s.account.isIRA,
        }
      : undefined
  );

  const { dispatcher } = useDispatcher();
  const globalSubAccountId = useStateSelector(subAccountIdSelector);
  const selectedSubAccount = values.transfer && values.transfer.subAccountId;

  useEffect(() => {
    dispatcher.balances.requestPendingTransfers();
    if (globalSubAccountId) {
      dispatcher.cashiering.balances.request(globalSubAccountId);
    } else if (selectedSubAccount && subAccounts) {
      const selectedSubAccountDetails = subAccounts.find(s => s.Id === selectedSubAccount);
      if (selectedSubAccountDetails && selectedSubAccountDetails.IsMaster === false) {
        dispatcher.cashiering.balances.request(selectedSubAccount);
      }
    } else {
      dispatcher.cashiering.balances.request();
    }
  }, []);

  const getAccountBalances = useCallback(
    (subaccountId?: number) => dispatcher.cashiering.balances.request(subaccountId),
    [dispatcher]
  );

  return (
    <>
      {totals && accountInfo ? (
        <>
          <DepositAmountViewContent
            {...props}
            getAccountBalances={getAccountBalances}
            totals={totals}
            {...accountInfo}
            scheduledTransferMode={scheduledTransferMode}
          />
        </>
      ) : (
        `Account details not loaded`
      )}
    </>
  );
};

export const useDepositAmountView = (): [typeof DepositAmountView, undefined, ViewOnValidateType] => {
  const hasSubAccounts = useSelector(account.hasSubAccounts);
  const isIraAccount = useStateSelector(account.accountIsIRA);
  const cashieringProfile = useStateSelector(s => s.cashiering.cashieringProfile);

  const onValidate = useCallback(
    (values: CashieringViewFormValues) => {
      const amountErrors = validateTransferAmount(values, CashieringRoute.Deposit, hasSubAccounts);

      if (isIraAccount) {
        const iraContributionErrors = validateIraContribution(values, cashieringProfile);
        return { ...amountErrors, ...iraContributionErrors };
      }

      return amountErrors;
    },
    [hasSubAccounts, isIraAccount]
  );

  return [DepositAmountView, undefined, onValidate];
};
